import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Divider,
  Loader,
  Center,
  Alert,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  Badge,
  Select,
  Collapse,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconInfoCircle,
  IconCheck,
  IconCopy,
  IconTrash,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconLoader,
  IconAlertCircle,
  IconX,
  IconHelp,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { differenceInHours } from 'date-fns';
import { api } from '@/services/api';
import { formatDateDistance, parseUTCDate } from '@/utils/date';
import { useState, useEffect } from 'react';
import { JobTable } from '@/components/JobTable';
import { AutoRefreshControls } from '@/components/AutoRefreshControls';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';

export function DatabaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [extendingTTL, setExtendingTTL] = useState(false);
  const [selectedTTL, setSelectedTTL] = useState('7d');
  const [showJobs, setShowJobs] = useState(false);
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ 
    defaultEnabled: true, 
    interval: 10000 
  });

  const {
    data: database,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['database', id],
    queryFn: () => api.getDatabase(id!),
    enabled: !!id,
    refetchInterval,
  });

  // Fetch job history for debugging
  const {
    data: jobs,
    isLoading: jobsLoading,
  } = useQuery({
    queryKey: ['database-jobs', id],
    queryFn: () => api.getDatabaseJobs(id!),
    enabled: !!id,
    refetchInterval, // Auto-refresh every 10s when database is creating/deleting
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteDatabase(id!),
    onSuccess: () => {
      notifications.show({
        title: 'Database Deleted',
        message: 'The database has been deleted successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      navigate('/databases');
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Failed to Delete Database',
        message: error.message || 'An unexpected error occurred',
        color: 'red',
      });
    },
  });

  const extendTTLMutation = useMutation({
    mutationFn: (ttl: string) => api.extendDatabaseTTL(id!, ttl),
    onSuccess: () => {
      notifications.show({
        title: 'TTL Extended',
        message: 'Database expiration time has been updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['database', id] });
      setExtendingTTL(false);
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Failed to Extend TTL',
        message: error.message || 'An unexpected error occurred',
        color: 'red',
      });
      setExtendingTTL(false);
    },
  });

  // Poll for status updates when database is in transitional state
  useEffect(() => {
    if (!database) return;
    
    const isTransitional = ['creating', 'provisioning', 'deleting'].includes(database.status);
    
    if (!isTransitional) return;
    
    // Poll every 5 seconds
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['database', id] });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [database?.status, id, queryClient]);

  // Helper function to get status display details
  const getStatusDetails = (status: string) => {
    switch (status) {
      case 'creating':
        return {
          color: 'blue',
          icon: <IconClock size={14} />,
          label: 'Creating Resources',
          description: 'Setting up Kubernetes PVC, Deployment, and Service'
        };
      case 'provisioning':
        return {
          color: 'cyan',
          icon: <IconLoader size={14} />,
          label: 'Provisioning',
          description: 'Kubernetes resources created, MySQL starting up'
        };
      case 'ready':
        return {
          color: 'green',
          icon: <IconCheck size={14} />,
          label: 'Ready',
          description: 'Database is ready to accept connections'
        };
      case 'deleting':
        return {
          color: 'orange',
          icon: <IconTrash size={14} />,
          label: 'Deleting',
          description: 'Removing database resources from cluster'
        };
      case 'deleted':
        return {
          color: 'gray',
          icon: <IconX size={14} />,
          label: 'Deleted',
          description: 'Database has been removed'
        };
      case 'failed':
        return {
          color: 'red',
          icon: <IconAlertCircle size={14} />,
          label: 'Failed',
          description: 'Database operation failed'
        };
      default:
        return {
          color: 'gray',
          icon: <IconHelp size={14} />,
          label: status,
          description: ''
        };
    }
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !database) {
    return (
      <Container my="md">
        <Alert title="Error" color="red" icon={<IconInfoCircle />}>
          Failed to load database: {(error as Error)?.message || 'Database not found'}
        </Alert>
      </Container>
    );
  }

  const expiresAt = parseUTCDate(database.expires_at);
  const hoursUntilExpiry = expiresAt ? differenceInHours(expiresAt, new Date()) : null;
  const isExpiringSoon =
    hoursUntilExpiry !== null && hoursUntilExpiry < 24 && hoursUntilExpiry > 0;
  const isExpired = hoursUntilExpiry !== null && hoursUntilExpiry <= 0;

  return (
    <Container size="md" py="md">
      <Group justify="space-between" mb="md">
        <Group>
          <ActionIcon variant="subtle" onClick={() => navigate('/databases')}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <div>
            <Title order={2}>{(database as any).name || database.id}</Title>
            <Text size="sm" c="dimmed">
              Database ID: {database.id}
            </Text>
          </div>
        </Group>
        <Group>
          <AutoRefreshControls
            enabled={autoRefresh}
            onEnabledChange={setAutoRefresh}
            intervalLabel="(10s)"
            onManualRefresh={refetch}
            isRefreshing={isLoading}
          />
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => deleteMutation.mutate()}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        {/* Database Info Card */}
        <Card withBorder>
          <Stack gap="md">
            <Text fw={500} size="lg">
              Database Information
            </Text>
            <Divider />

            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Status
              </Text>
              <Group gap="xs">
                <Badge
                  color={getStatusDetails(database.status).color}
                  variant="light"
                  leftSection={getStatusDetails(database.status).icon}
                >
                  {getStatusDetails(database.status).label}
                </Badge>
                {getStatusDetails(database.status).description && (
                  <Text size="xs" c="dimmed">
                    {getStatusDetails(database.status).description}
                  </Text>
                )}
              </Group>
            </Stack>

            {database.status_message && (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Status Message
                </Text>
                <Text size="sm">{database.status_message}</Text>
              </Stack>
            )}

            <Divider />

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Snapshot
              </Text>
              <Code>{database.snapshot}</Code>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Created
              </Text>
              <Text size="sm">
                {database.created_at
                  ? formatDateDistance(database.created_at)
                  : '—'}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Expires
              </Text>
              {expiresAt ? (
                <Badge
                  color={isExpired ? 'red' : isExpiringSoon ? 'yellow' : 'gray'}
                  variant="light"
                >
                  {isExpired
                    ? 'Expired'
                    : formatDateDistance(database.expires_at)}
                </Badge>
              ) : (
                <Text size="sm">Never</Text>
              )}
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Owner
              </Text>
              <Text size="sm">{database.owner_github_login || '—'}</Text>
            </Group>

            <Divider />

            {/* TTL Extension */}
            {!extendingTTL ? (
              <Button
                variant="light"
                fullWidth
                onClick={() => setExtendingTTL(true)}
              >
                Extend TTL
              </Button>
            ) : (
              <Stack gap="sm">
                <Select
                  label="New TTL"
                  data={[
                    { value: '1h', label: '1 hour' },
                    { value: '4h', label: '4 hours' },
                    { value: '24h', label: '24 hours (1 day)' },
                    { value: '7d', label: '7 days (1 week)' },
                    { value: '30d', label: '30 days (1 month)' },
                  ]}
                  value={selectedTTL}
                  onChange={(value) => setSelectedTTL(value || '7d')}
                />
                <Group justify="flex-end">
                  <Button
                    variant="subtle"
                    onClick={() => setExtendingTTL(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => extendTTLMutation.mutate(selectedTTL)}
                    loading={extendTTLMutation.isPending}
                  >
                    Apply
                  </Button>
                </Group>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Connection Info Card */}
        <Card withBorder>
          <Stack gap="md">
            <Text fw={500} size="lg">
              Connection Information
            </Text>
            <Divider />

            {database.status === 'ready' && (database.connection_data || database.connection_host) ? (
              <>
                {/* Parse connection_data JSON if available */}
                {(() => {
                  const connectionData = (database as any).connection_data 
                    ? JSON.parse((database as any).connection_data) 
                    : null;

                  // Teleport-based connection
                  if (connectionData?.method === 'teleport') {
                    return (
                      <>
                        <Alert color="blue" title="Teleport Connection" icon={<IconInfoCircle size={16} />}>
                          <Text size="sm">
                            This database uses Teleport for secure access. Follow the commands below to connect.
                          </Text>
                        </Alert>

                        {/* List command */}
                        {connectionData.commands?.list && (
                          <Group justify="space-between" align="flex-start">
                            <Stack gap={4} style={{ flex: 1 }}>
                              <Text size="sm" c="dimmed">
                                1. List Available Databases
                              </Text>
                              <Code style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                {connectionData.commands.list}
                              </Code>
                            </Stack>
                            <CopyButton value={connectionData.commands.list}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                  <ActionIcon
                                    color={copied ? 'teal' : 'gray'}
                                    variant="subtle"
                                    onClick={copy}
                                  >
                                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                        )}

                        {/* Proxy command */}
                        {connectionData.commands?.proxy && (
                          <Group justify="space-between" align="flex-start">
                            <Stack gap={4} style={{ flex: 1 }}>
                              <Text size="sm" c="dimmed">
                                2. Start Teleport Proxy
                              </Text>
                              <Code style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                {connectionData.commands.proxy}
                              </Code>
                            </Stack>
                            <CopyButton value={connectionData.commands.proxy}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                  <ActionIcon
                                    color={copied ? 'teal' : 'gray'}
                                    variant="subtle"
                                    onClick={copy}
                                  >
                                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                        )}

                        {/* Connect command */}
                        {connectionData.commands?.connect && (
                          <Group justify="space-between" align="flex-start">
                            <Stack gap={4} style={{ flex: 1 }}>
                              <Text size="sm" c="dimmed">
                                3. Connect to Database
                              </Text>
                              <Code style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                {connectionData.commands.connect}
                              </Code>
                            </Stack>
                            <CopyButton value={connectionData.commands.connect}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                  <ActionIcon
                                    color={copied ? 'teal' : 'gray'}
                                    variant="subtle"
                                    onClick={copy}
                                  >
                                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Group>
                        )}
                      </>
                    );
                  }

                  // Traditional direct connection
                  return (
                    <>
                      {/* Host */}
                      {database.connection_host && (
                        <Group justify="space-between">
                          <Stack gap={4} style={{ flex: 1 }}>
                            <Text size="sm" c="dimmed">
                              Host
                            </Text>
                            <Code>{database.connection_host}</Code>
                          </Stack>
                          <CopyButton value={database.connection_host}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                <ActionIcon
                                  color={copied ? 'teal' : 'gray'}
                                  variant="subtle"
                                  onClick={copy}
                                >
                                  {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Group>
                      )}

                      {/* Port */}
                      {database.connection_port && (
                        <Group justify="space-between">
                          <Stack gap={4} style={{ flex: 1 }}>
                            <Text size="sm" c="dimmed">
                              Port
                            </Text>
                            <Code>{database.connection_port}</Code>
                          </Stack>
                          <CopyButton value={database.connection_port.toString()}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                <ActionIcon
                                  color={copied ? 'teal' : 'gray'}
                                  variant="subtle"
                                  onClick={copy}
                                >
                                  {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Group>
                      )}

                      {/* Connection URI */}
                      {database.connection_uri && (
                        <Group justify="space-between" align="flex-start">
                          <Stack gap={4} style={{ flex: 1 }}>
                            <Text size="sm" c="dimmed">
                              Connection URI
                            </Text>
                            <Code style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                              {database.connection_uri}
                            </Code>
                          </Stack>
                          <CopyButton value={database.connection_uri}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied' : 'Copy'}>
                                <ActionIcon
                                  color={copied ? 'teal' : 'gray'}
                                  variant="subtle"
                                  onClick={copy}
                                >
                                  {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Group>
                      )}
                    </>
                  );
                })()}

                <Alert icon={<IconInfoCircle />} title="Shared Credentials" color="blue">
                  <Text size="sm">
                    The database uses shared credentials stored in the Kubernetes cluster. 
                    Credentials are: <Code>boppity</Code> / <Code>[see cluster secret]</Code>
                  </Text>
                </Alert>
              </>
            ) : (
              <Alert icon={<IconInfoCircle />} title="Connection Not Ready" color="yellow">
                <Text size="sm">
                  Connection information will be available once the database is ready.
                  Current status: {database.status}
                </Text>
              </Alert>
            )}
          </Stack>
        </Card>

        {/* Job Debugging (Collapsible) */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={() => setShowJobs(!showJobs)}>
              <Text fw={500} size="lg">
                Job History (Debug)
              </Text>
              {showJobs ? <IconChevronDown size={20} /> : <IconChevronRight size={20} />}
            </Group>

            <Collapse in={showJobs}>
              <Divider mb="md" />
              
              {jobsLoading ? (
                <Center><Loader size="sm" /></Center>
              ) : jobs && jobs.length > 0 ? (
                <JobTable 
                  jobs={jobs}
                  showEnvColumn={false}
                  showUserColumn={false}
                />
              ) : (
                <Text size="sm" c="dimmed">No jobs found</Text>
              )}
            </Collapse>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}

